import { Icon, Layout, Menu, Spin } from 'antd';
import { NavLink, Redirect, Route, RouteComponentProps, withRouter } from "react-router-dom";
import * as React from 'react';
import { config } from "./apis/api";
import { validateToken } from "./apis/Auth";
import AclManagement from './components/AclManagement/AclManagement';
import Companies from "./components/Companies/Companies";
import PlanPage from "./components/Companies/Plan/PlanPage";
import Dashboard from "./components/Dashboard";
import LoginForm from "./components/LoginForm";
import Logs from "./components/Logs";
import ChargeHistory from "./components/Reporting/ChargeHistory";
import Terminations from './components/Terminations';

import './App.scss';
import './assets/css/submenu.css';
import 'antd/dist/antd.css';

const { Content, Header } = Layout;

interface IAppProps extends RouteComponentProps<{}> {
}

// We aren't using redux to manage our state in this app, so we need to manually
// define the state variables we care about. Since we're using typescript, the
// name isn't enough, we have to define the appropriate type. If a boolean variable has
// the potential to be null, define it like so: 'varName: boolean | null'
interface IAppState {
  authorized: boolean,
  currentNavItems: string[],
  isProcessing: boolean,  // Used to determine if we're processing something at the moment
  token: string,
}

class App extends React.Component<IAppProps, IAppState>  {
  // Any initialization needs to happen here!
  constructor(props: any) {
    super(props);
    const storedToken = localStorage.getItem('token');

    let processing = true;
    if (storedToken) {
      validateToken(storedToken).then((result) => {
        if (result.valid) {
          // Since we only need the user ID, we will assume the user is active and provide the ID that was stored with the token that we just validated
          this.setState({
            authorized: true,
            isProcessing: false,
            token: storedToken,
          });
        } else {
          this.setState({
            isProcessing: false,
          });
        }
      });
    } else {
      processing = false;
    }

    // Set your state to its initial value
    this.state = this.initialState(processing);
  }

  public componentDidMount()
  {
    this.setState({
      currentNavItems: this.props.location.pathname === '/' ? ['/dashboard'] : [this.props.location.pathname],
    });
  }

  // Once we receive state variables from LoginForm.tsx, what do we plan
  // to do with them? We manually set our state in the parent component.
  public onLogin(isAuthorized: boolean, authToken: string) {
    localStorage.setItem('token', authToken);
    config.authToken = authToken;

    this.setState({
        authorized: isAuthorized,
        isProcessing: false,
        token: authToken,
    });
  }

  public logout = () => {
    localStorage.removeItem('token');
    const resetState = this.initialState(false);
    this.setState(resetState);
  }

  public initialState = (processingStatus: boolean) => (
    {
      authorized: false,
      currentNavItems: [],
      isProcessing: processingStatus,
      token: '',
      user: null,
    }
  );

  public render() {
    if (this.state.isProcessing) {
      return (<Spin style={{
        paddingLeft: '50%',
        paddingTop: '20%',
      }} size="large"/>);
    }

    // The default <LoginForm> component from ant doesn't have any method for passing
    // state back and forth, so we need to manually set that up, or 'wire' it.
    const WiredLoginForm = (props: any) => {
      // Define our callback function, which communicates with `public onLogin` in this file
      // above the render function

      const onLoginFunc = (isAuthorized: boolean, authToken: string) => {
        this.onLogin(isAuthorized, authToken);
      }

      return (
        <LoginForm
          onLogin={onLoginFunc} // Where the magic happens; how we read state from our child component
          {...props}
        />
      );
    };

    // Define the default, logged-in nav menu
    let navMenu = (
      <Menu
        theme="dark"
        mode="horizontal"
        defaultSelectedKeys={this.state.currentNavItems}
        style={{ lineHeight: '64px', backgroundColor: '#ACE86B', borderBottomColor: '#ACE86B', fontWeight: 'bold', fontSize: 15 }}
      >
        <Menu.Item key="/dashboard"><NavLink to="/"><Icon type="dashboard" />Dashboard</NavLink></Menu.Item>
        <Menu.Item key="/companies"><NavLink to="/companies">Companies</NavLink></Menu.Item>
        <Menu.Item key="/terminations"><NavLink to="/terminations">Terminations</NavLink></Menu.Item>
        <Menu.Item key="/logout" style={{color: '#616161',}} onClick={this.logout}>Log Out</Menu.Item>
      </Menu>
    );

    // Future improvement: use config.authToken in these components and stop the prop drilling
    const aclManagementRoute = (props: any) => (
      <AclManagement
          {...props}
      />
    );
    const chargeHistoryRoute = (props: RouteComponentProps<any>) => (
        <ChargeHistory
            companyID={parseInt(props.match.params.companyID, 10)}
            token={this.state.token}
        />
    );
    const companiesRoute = () => <Companies logoutFunc={this.logout} token={this.state.token} />
    const logsRoute = () => <Logs token={this.state.token} />
    const terminationsRoute = () => <Terminations logoutFunc={this.logout} />
    const planPageRoute = (props: RouteComponentProps<any>) => (
      <PlanPage companyID={parseInt(props.match.params.companyID, 10)} />
    )

    // Define logged-in routes
    let routes = (
        <div style={{ background: '#fff', padding: 24, minHeight: 280 }}>
            <Route exact={true} path="/" component={Dashboard}/>
            <Route exact={true} path="/aclManagement/:companyID" render={aclManagementRoute}/>
            <Route exact={true} path="/companies/:companyID/plan" component={planPageRoute} />
            <Route exact={true} path="/companies" render={companiesRoute}/>
            { config.environment !== 'production' && <Route exact={true} path="/logs" render={logsRoute}/> }
            <Route exact={true} path="/reporting/chargeHistory/:companyID" render={chargeHistoryRoute}/>
            <Route exact={true} path="/terminations" component={terminationsRoute} />
        </div>
    );

    // If our visitor is not logged in, display login page and remove nav options
    if (!this.state.authorized) {
      navMenu = (
        <Menu
          theme="dark"
          mode="horizontal"
          defaultSelectedKeys={this.state.currentNavItems}
          style={{ lineHeight: '64px', backgroundColor: '#ACE86B', borderBottomColor: '#ACE86B', fontWeight: 'bold', fontSize: 15 }}
        />
      );

      // If you are not logged in, every page redirects you to the
      // main login form
      routes = (<div style={{ background: '#fff', padding: 24, minHeight: 400 }}>
        <Route exact={true} path="/" component={WiredLoginForm}/>
        {this.props.location.pathname !== '/site' &&
          <Redirect to="/"/>
        }
      </div>);
    }

    return (
      <div className="App">
        <Layout className="layout">
          <Header style={{ backgroundColor: '#ACE86B' }}>
            <div className="logo" />
            {navMenu}
          </Header>
          <Content style={{ padding: '12px', backgroundColor: '#effde1' }}>
            {/* // Commenting out until we have a need for breadcrumbs
              <Breadcrumb style={{ margin: '10px 0' }}>
              <Breadcrumb.Item>Link 1</Breadcrumb.Item>
              <Breadcrumb.Item>Link 2</Breadcrumb.Item>
              <Breadcrumb.Item>Link 3</Breadcrumb.Item>
            </Breadcrumb> */}
            {routes}
          </Content>
        </Layout>
      </div>
    );
  }
}

export default withRouter(App);
